<template>
    <div class="relative">
        <label
            class="relative"
            :title="`${modelValue?.card?.title} (${modelValue?.last_four})`"
            v-click-outside="closeDropdown"
        >
            <input
                class="h-9 w-full flex items-center gap-2 text-left pl-3 text-slate-500 relative bg-white rounded border border-slate-200 py-1.5 placeholder:text-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed pr-8"
                :disabled="disabled"
                v-model="cardQuery"
                @click="toggleDropdown"
                @keydown.enter.prevent="enter"
                @keydown.down="down"
                @keydown.up="up"
                @keydown.esc.prevent="closeDropdown"
                ref="cardDropdownButton"
                autocomplete="off"
            />
            <Icon
                icon="chevron-down"
                class="block leading-none text-slate-500 w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                :class="{ 'rotate-180 transition-transform': showCardsList }"
            />
            <span
                class="w-full flex items-center gap-2 absolute pr-14 left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm whitespace-nowrap overflow-hidden"
                v-if="(!cardQuery && !showCardsList) || modelValue"
            >
                <template v-if="modelValue">
                    <div class="flex-shrink-0">
                        <ImageLoader class="block rounded-sm">
                            <template #image>
                                <img
                                    :src="`${cdnUrl}/${modelValue?.card?.card_image?.path}/_small/${modelValue?.card?.card_image?.file_name}`"
                                    class="h-5 w-[31.72px] rounded-sm"
                                    loading="eager"
                                />
                            </template>
                            <template #preloader>
                                <div
                                    class="h-5 w-[31.72px] rounded-sm bg-slate-100"
                                ></div>
                            </template>
                            <template #error>
                                <Icon icon="credit-card" class="w-8 h-5" />
                            </template>
                        </ImageLoader>
                    </div>
                    <div class="flex-1 min-w-0 flex items-center gap-1">
                        <span class="truncate">
                            {{ modelValue?.card?.title }}
                        </span>
                        <span
                            class="flex shrink-0"
                            v-if="modelValue?.last_four"
                        >
                            ({{ modelValue?.last_four }})
                        </span>
                    </div>
                </template>
                <template v-else>
                    <div class="flex-shrink-0">
                        <svg
                            width="32"
                            height="20"
                            viewBox="0 0 32 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <rect
                                id="rectangle"
                                width="32"
                                height="20"
                                rx="2"
                                fill="#E2E8F0"
                            />
                            <rect
                                id="rectangle_2"
                                y="4"
                                width="32"
                                height="5"
                                fill="#CBD5E1"
                            />

                            <defs>
                                <clipPath id="clip0_2121_13192">
                                    <rect
                                        width="32"
                                        height="20"
                                        rx="2"
                                        fill="white"
                                    />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    Select card
                </template>
            </span>
        </label>
        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white rounded-lg shadow-sm border border-slate-200 absolute z-[99999]"
                    v-show="showCardsList"
                    ref="dropdownContainer"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <template
                        v-for="(walletUser, i) in filteredWalletUsers"
                        :key="walletUser.id"
                    >
                        <p
                            :class="{
                                'mt-6': i === filteredWalletUsers.length - 1,
                            }"
                            class="flex items-center gap-2 text-slate-500 text-sm font-medium p-2"
                        >
                            <Icon icon="user" class="w-5 h-5" />
                            {{ walletUser.name }}
                        </p>
                        <div
                            v-for="(institution, i) in walletUser.institutions"
                            :key="institution.id"
                            :class="{ 'mt-2': i > 0 }"
                        >
                            <p
                                class="text-slate-800 font-medium leading-tight text-sm p-2"
                            >
                                {{ institution.issuing_bank.name }}
                            </p>
                            <div>
                                <button
                                    v-for="account in institution.accounts"
                                    :key="account.id"
                                    class="w-full flex items-center gap-2 p-2 text-slate-800 text-sm rounded hover:bg-slate-100 transition-colors cursor-pointer"
                                    :class="{
                                        'bg-slate-100':
                                            activeIndex >= 0 &&
                                            flattenedCards[activeIndex]?.id ==
                                                account.id,
                                        'font-medium':
                                            modelValue?.id === account.id,
                                    }"
                                    :title="`${account.card?.title} (${account.last_four})`"
                                    @click="selectCard(account, walletUser)"
                                    id="hs-wallet-select-card"
                                >
                                    <div class="flex-shrink-0">
                                        <ImageLoader>
                                            <template #image>
                                                <img
                                                    :src="`${cdnUrl}/${account.card?.card_image?.path}/_small/${account.card?.card_image?.file_name}`"
                                                    :alt="account?.card?.title"
                                                    class="h-5 w-[31.72px] rounded-sm object-contain"
                                                    loading="lazy"
                                                />
                                            </template>
                                            <template #preloader>
                                                <div
                                                    class="h-5 w-[31.72px] rounded-sm bg-slate-100"
                                                ></div>
                                            </template>
                                            <template #error>
                                                <Icon
                                                    icon="credit-card"
                                                    class="w-8 h-5"
                                                />
                                            </template>
                                        </ImageLoader>
                                    </div>
                                    <div
                                        class="flex-1 min-w-0 flex items-center gap-1"
                                    >
                                        <span class="truncate">
                                            {{ account.card?.title }}
                                        </span>
                                        <span>
                                            {{
                                                account?.last_four
                                                    ? `(${account?.last_four})`
                                                    : ''
                                            }}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </template>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
    computed,
    nextTick,
} from 'vue';
import { useWalletStore } from '@/store/wallet';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    modelValue: {
        type: Object,
        default: null,
    },
    excludeCard: {
        type: Object,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const walletStore = useWalletStore();
const { wallet } = storeToRefs(walletStore);

const cardQuery = ref(null);
const activeIndex = ref(-1);
const showCardsList = ref(false);
const cardDropdownButton = useTemplateRef('cardDropdownButton');
const dropdownContainer = useTemplateRef('dropdownContainer');
const top = ref(0);
const left = ref(0);
const width = ref(80);

// Debounce the search to prevent excessive re-renders
const debouncedCardQuery = computed(() => {
    return cardQuery.value;
});

const cdnUrl = computed(() => window.cdnUrl);

const filteredWalletUsers = computed(() => {
    if (!debouncedCardQuery.value) {
        // Apply exclude filter even when there's no search
        return filterWalletUsers(wallet.value.users);
    }

    const query = debouncedCardQuery.value.toLowerCase();
    return filterWalletUsers(wallet.value.users, query);
});

const flattenedCards = computed(() => {
    const cards = filteredWalletUsers.value.reduce((arr, user) => {
        user.institutions.forEach((institution) => {
            institution.accounts.forEach((account) => {
                arr.push(account);
            });
        });
        return arr;
    }, []);
    return cards;
});

const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!showCardsList.value) {
        openDropdown();
    } else {
        setTimeout(() => {
            showCardsList.value = false;
        }, 100);
    }
};

const closeDropdown = () => {
    showCardsList.value = false;
    activeIndex.value = -1;
};

const openDropdown = () => {
    showCardsList.value = true;
    activeIndex.value = 0;
};

const enter = () => {
    const selected = flattenedCards.value[activeIndex.value];
    if (selected) {
        selectCard(selected, selected.walletUser);
    }
};

const up = () => {
    if (showCardsList.value) {
        activeIndex.value = Math.max(activeIndex.value - 1, -1);
        scrollActiveItemIntoView();
    }
};

const down = () => {
    if (!showCardsList.value) {
        showCardsList.value = true;
        activeIndex.value = 0;
    } else {
        activeIndex.value = Math.min(
            activeIndex.value + 1,
            flattenedCards.value.length - 1
        );
    }

    scrollActiveItemIntoView();
};

const scrollActiveItemIntoView = () => {
    if (activeIndex.value >= 0) {
        nextTick(() => {
            const container = dropdownContainer.value;
            const activeItem = container?.querySelector('.bg-slate-100');
            if (container && activeItem) {
                const containerRect = container.getBoundingClientRect();
                const itemRect = activeItem.getBoundingClientRect();

                if (itemRect.bottom > containerRect.bottom) {
                    activeItem.scrollIntoView({
                        block: 'end',
                        behavior: 'smooth',
                    });
                } else if (itemRect.top < containerRect.top) {
                    activeItem.scrollIntoView({
                        block: 'start',
                        behavior: 'smooth',
                    });
                }
            }
        });
    }
};

const filterWalletUsers = (users, query = '') => {
    const results = [];

    for (const user of users) {
        const filteredInstitutions = [];

        for (const institution of user.institutions) {
            const filteredAccounts = institution.accounts.filter((account) => {
                // First check if this account should be excluded
                if (account.id === props.excludeCard?.id) {
                    return false;
                }

                // If there's a search query, apply the search filters
                if (query) {
                    return (
                        account.card?.title?.toLowerCase().includes(query) ||
                        account.last_four?.includes(query) ||
                        institution.issuing_bank.name
                            ?.toLowerCase()
                            .includes(query)
                    );
                }

                return true;
            });

            if (filteredAccounts.length > 0) {
                filteredInstitutions.push({
                    ...institution,
                    accounts: filteredAccounts,
                });
            }
        }

        if (filteredInstitutions.length > 0) {
            results.push({
                ...user,
                institutions: filteredInstitutions,
            });
        }
    }

    return results;
};

const setDropdownPosition = () => {
    const position = cardDropdownButton.value.getBoundingClientRect();
    top.value = position.top + 42;
    left.value = position.left;
    width.value = position.width;
};

watch(showCardsList, (val) => {
    if (val) {
        setDropdownPosition();
    }
});

const selectCard = (account, user) => {
    emit('update:modelValue', { account, user });
    cardQuery.value = '';
    showCardsList.value = false;
};

onMounted(() => {
    window.addEventListener('resize', setDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        ?.addEventListener('scroll', setDropdownPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        ?.removeEventListener('scroll', setDropdownPosition);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-from {
    opacity: 0;
}
</style>
